import { EditorState, useEditor, useNode } from "@craftjs/core";
import * as React from "react";

import { useDuplicateNode } from "@/components/editor/utils/use-duplicate-node";
import { ArrowDownLong } from "@/components/icons/arrow-down-long";
import { CopySharp } from "@/components/icons/copy-sharp";
import { TrashFilled } from "@/components/icons/trash-filled";
import { logProductAnalyticsEvent } from "@/features/analytics";
import { useAppDispatch } from "@/redux/store";
import getCurrentNodeId from "@/utils/get-current-node-id";

import {
  BlockOutlineFloatingOptionButton,
  BlockOutlineFloatingOptionsWrapper,
} from "./block-outline-styles";

export default function BlockFloatingOptions() {
  const dispatch = useAppDispatch();
  const { nodeId, deletable } = useNode((node) => {
    return {
      nodeId: node.id,
      deletable: node.getParent().getChildNodes().length > 1,
    };
  });

  const {
    actions: { delete: deleteNode, move: moveNode },
    moveUp,
    moveDown,
    getNodes,
  } = useEditor((query) => {
    const moveUp = getMove(query.getState(), "up");
    const moveDown = getMove(query.getState(), "down");
    const getNodes = () => query.getState().nodes;

    return {
      moveUp,
      moveDown,
      getNodes,
    };
  });

  const { duplicate } = useDuplicateNode();

  return (
    <BlockOutlineFloatingOptionsWrapper>
      <BlockOutlineFloatingOptionButton
        data-testid="block-outline-copy"
        onClick={() => duplicate(nodeId)}
      >
        <CopySharp className="inline size-5 text-white" />
      </BlockOutlineFloatingOptionButton>

      {moveUp.canMove && (
        <BlockOutlineFloatingOptionButton
          data-testid="block-outline-arrow-upward"
          onClick={() =>
            moveNode(
              moveUp.currentNodeId,
              moveUp.parentNodeId,
              moveUp.targetIndex
            )
          }
        >
          <ArrowDownLong className="inline size-5 rotate-180 text-white" />
        </BlockOutlineFloatingOptionButton>
      )}

      {moveDown.canMove && (
        <BlockOutlineFloatingOptionButton
          data-testid="block-outline-arrow-downward"
          onClick={() =>
            moveNode(
              moveDown.currentNodeId,
              moveDown.parentNodeId,
              moveDown.targetIndex + 1
            )
          }
        >
          <ArrowDownLong className="inline size-5 text-white" />
        </BlockOutlineFloatingOptionButton>
      )}

      {deletable && (
        <BlockOutlineFloatingOptionButton
          data-testid="block-outline-delete-outlined"
          onClick={() => {
            deleteNode(nodeId);
            dispatch({
              type: "[page editor] page content updated",
              payload: {
                content: getNodes(),
              },
            });
            logProductAnalyticsEvent({ name: "ch_canvas_block_remove" });
          }}
        >
          <TrashFilled className="inline size-5 text-white" />
        </BlockOutlineFloatingOptionButton>
      )}
    </BlockOutlineFloatingOptionsWrapper>
  );
}

export function getMove(state: EditorState, direction: "up" | "down") {
  const currentNodeId = getCurrentNodeId(state);
  const parentNodeId = state.nodes[currentNodeId]?.parent;
  const parentNode = parentNodeId && state.nodes[parentNodeId];
  const adder = direction === "up" ? -1 : 1;
  const targetIndex =
    parentNode?.nodes.findIndex((nodeId) => nodeId === currentNodeId) + adder;
  const move = {
    canMove:
      direction === "up"
        ? targetIndex > -1
        : targetIndex < parentNode?.nodes.length,
    currentNodeId,
    parentNodeId,
    targetIndex,
  };

  return move;
}
